import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Container,
  Chip,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Stack,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  AccessTime as ClockIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { holidayService } from '../services/holidayService';
import { employeeService } from '../services/employeeService';
import {
  PhilippineHoliday,
  HolidayWorkRecord,
  HolidayPaySummary,
  formatHolidayType,
  getHolidayPayDescription
} from '../types/holiday';
import { Employee } from '../types';

interface HolidayWorkFormData {
  employee_id: string;
  holiday_id: string;
  date: string;
  hours_worked: number;
  notes: string;
}

const HolidayManagement: React.FC = () => {
  const [holidays, setHolidays] = useState<PhilippineHoliday[]>([]);
  const [holidayWorkRecords, setHolidayWorkRecords] = useState<HolidayWorkRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  // Removed selectedYear - holidays now work year-independently
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [showHolidayWorkDialog, setShowHolidayWorkDialog] = useState(false);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<PhilippineHoliday | null>(null);
  const [selectedHolidayWork, setSelectedHolidayWork] = useState<HolidayWorkRecord | null>(null);
  const [tabValue, setTabValue] = useState<string>('holidays');
  
  // Form states
  const [holidayWorkForm, setHolidayWorkForm] = useState<HolidayWorkFormData>({
    employee_id: '',
    holiday_id: '',
    date: '',
    hours_worked: 8,
    notes: ''
  });
  
  const [holidayForm, setHolidayForm] = useState<Partial<PhilippineHoliday>>({
    name: '',
    date: '',
    year: new Date().getFullYear(), // Default to current year
    type: 'regular',
    description: '',
    is_nationwide: true
  });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [dateRange, setDateRange] = useState({
    start: `${new Date().getFullYear()}-01-01`,
    end: `${new Date().getFullYear()}-12-31`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading all holidays...');
    
    const [holidaysData, holidayWorkData, employeesData] = await Promise.all([
         holidayService.getAllHolidays(), // Load all holidays instead of year-specific
         holidayService.getAllHolidayWork('2020-01-01', '2030-12-31'), // Load all holiday work records with wide date range
         employeeService.getAll()
      ]);
      
      console.log('Holidays loaded:', holidaysData.length, holidaysData);
      setHolidays(holidaysData);
      setEmployees(employeesData.filter(emp => emp.status === 'active'));
      setHolidayWorkRecords(holidayWorkData);
      
      if (holidaysData.length === 0) {
        setError('No holidays found for the selected year. Please check if the database is properly set up.');
      }
    } catch (err) {
      setError('Failed to load data: ' + (err as Error).message);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecordHolidayWork = async () => {
    if (!holidayWorkForm.employee_id || !holidayWorkForm.holiday_id || !holidayWorkForm.date) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const employee = employees.find(emp => emp.id === holidayWorkForm.employee_id);
      if (!employee) {
        setError('Employee not found');
        return;
      }

      const hourlyRate = employee.hourly_rate || (employee.salary / (22 * 8));
      
      const result = await holidayService.recordHolidayWork(
        holidayWorkForm.employee_id,
        holidayWorkForm.holiday_id,
        holidayWorkForm.date,
        holidayWorkForm.hours_worked,
        hourlyRate,
        holidayWorkForm.notes
      );

      if (result) {
        setSuccess('Holiday work recorded successfully');
        setShowHolidayWorkDialog(false);
        setHolidayWorkForm({
          employee_id: '',
          holiday_id: '',
          date: '',
          hours_worked: 8,
          notes: ''
        });
        loadData();
      } else {
        setError('Failed to record holiday work');
      }
    } catch (err) {
      setError('Error recording holiday work');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date || !holidayForm.type) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const result = await holidayService.addHoliday({
        name: holidayForm.name!,
        date: holidayForm.date!,
        year: new Date(holidayForm.date!).getFullYear(),
        type: holidayForm.type!,
        description: holidayForm.description || '',
        is_nationwide: holidayForm.is_nationwide || true
      });

      if (result) {
        setSuccess('Holiday added successfully');
        setShowHolidayDialog(false);
        setHolidayForm({
          name: '',
          date: '',
          year: new Date().getFullYear(), // Default to current year
          type: 'regular',
          description: '',
          is_nationwide: true
        });
        loadData();
      } else {
        setError('Failed to add holiday');
      }
    } catch (err) {
      setError('Error adding holiday');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHolidayWork = async (recordId: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this holiday work record?');
    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const success = await holidayService.deleteHolidayWorkRecord(recordId);
      if (success) {
        setSuccess('Holiday work record deleted successfully');
        loadData();
      } else {
        setError('Failed to delete holiday work record');
      }
    } catch (err) {
      setError('Error deleting holiday work record');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredHolidays = holidays.filter(holiday => {
    const matchesSearch = holiday.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || holiday.type === filterType;
    return matchesSearch && matchesType;
  });

  const filteredHolidayWork = holidayWorkRecords.filter(record => {
    const employeeName = record.employee ? 
      `${record.employee.first_name} ${record.employee.last_name}`.toLowerCase() : '';
    const holidayName = record.holiday?.name.toLowerCase() || '';
    const matchesSearch = employeeName.includes(searchTerm.toLowerCase()) || 
                         holidayName.includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const getHolidayTypeBadge = (type: PhilippineHoliday['type']) => {
    const colors = {
      regular: 'error',
      special_non_working: 'default',
      special_working: 'primary'
    } as const;
    
    return (
      <Chip 
        label={formatHolidayType(type)}
        color={colors[type]}
        size="small"
      />
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Holiday Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage Philippine holidays and track holiday work compensation
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Year dropdown removed - holidays now work year-independently */}
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Holiday Logic Information */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            🎯 Smart Holiday Detection Active
          </Typography>
          <Typography variant="body2">
            The system now calculates holiday pay based on <strong>month and day only</strong>, regardless of year. 
            This means employees will receive proper holiday compensation even if working on holidays from any year 
            (e.g., New Year's Day 2026 will be recognized as a holiday based on January 1st pattern).
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              icon={<MoneyIcon />} 
              label="Regular Holidays: 200% Pay" 
              color="error" 
              size="small" 
            />
            <Chip 
              icon={<MoneyIcon />} 
              label="Special Non-Working: 130% Pay" 
              color="default" 
              size="small" 
            />
            <Chip 
              icon={<MoneyIcon />} 
              label="Special Working: 130% Pay" 
              color="primary" 
              size="small" 
            />
          </Box>
        </Box>
      </Alert>

      <Box sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Philippine Holidays" value="holidays" />
          <Tab label="Holiday Work Records" value="holiday-work" />
        </Tabs>

        {/* Holidays Tab */}
        {tabValue === 'holidays' && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Philippine Holidays
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Official holidays in the Philippines with pay rates
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowHolidayDialog(true)}
                >
                  Add Holiday
                </Button>
              </Box>

              {/* Holiday Dialog */}
              <Dialog open={showHolidayDialog} onClose={() => setShowHolidayDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Holiday</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Add a new holiday to the Philippine calendar
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      label="Holiday Name"
                      value={holidayForm.name || ''}
                      onChange={(e) => setHolidayForm({...holidayForm, name: e.target.value})}
                      placeholder="e.g., Independence Day"
                      fullWidth
                    />
                    <TextField
                      label="Date"
                      type="date"
                      value={holidayForm.date || ''}
                      onChange={(e) => setHolidayForm({...holidayForm, date: e.target.value})}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel>Holiday Type</InputLabel>
                      <Select
                        value={holidayForm.type || 'regular'}
                        onChange={(e) => setHolidayForm({...holidayForm, type: e.target.value as PhilippineHoliday['type']})}
                        label="Holiday Type"
                      >
                        <MenuItem value="regular">Regular Holiday (200% pay)</MenuItem>
                        <MenuItem value="special_non_working">Special Non-Working (130% pay if worked)</MenuItem>
                        <MenuItem value="special_working">Special Working (100% normal pay)</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Description"
                      value={holidayForm.description || ''}
                      onChange={(e) => setHolidayForm({...holidayForm, description: e.target.value})}
                      placeholder="Holiday description..."
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowHolidayDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddHoliday} disabled={loading} variant="contained">
                    Add Holiday
                  </Button>
                </DialogActions>
              </Dialog>
              {/* Search and Filter */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  placeholder="Search holidays..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Filter Type</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    label="Filter Type"
                    
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="regular">Regular Holidays</MenuItem>
                    <MenuItem value="special_non_working">Special Non-Working</MenuItem>
                    <MenuItem value="special_working">Special Working</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Holidays Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Holiday Name</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Pay Rate</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={24} sx={{ mr: 2 }} />
                          Loading holidays...
                        </TableCell>
                      </TableRow>
                    ) : filteredHolidays.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {holidays.length === 0 
                              ? 'No holidays found for this year. Please check database setup.' 
                              : 'No holidays match the current filter criteria.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredHolidays.map((holiday) => (
                        <TableRow key={holiday.id}>
                          <TableCell sx={{ fontWeight: 'medium' }}>{holiday.name}</TableCell>
                          <TableCell>
                            {new Date(holiday.date).toLocaleDateString('en-PH', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </TableCell>
                          <TableCell>{getHolidayTypeBadge(holiday.type)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {getHolidayPayDescription(holiday.type)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {holiday.description}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Holiday Work Records Tab */}
        {tabValue === 'holiday-work' && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Holiday Work Records
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track employees who worked on holidays and their compensation
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowHolidayWorkDialog(true)}
                >
                  Record Holiday Work
                </Button>
              </Box>

              {/* Holiday Work Dialog */}
              <Dialog open={showHolidayWorkDialog} onClose={() => setShowHolidayWorkDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Record Holiday Work</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Record an employee's work on a holiday for proper compensation
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel>Employee</InputLabel>
                      <Select
                        value={holidayWorkForm.employee_id}
                        onChange={(e) => setHolidayWorkForm({...holidayWorkForm, employee_id: e.target.value})}
                        label="Employee"
                      >
                        {employees.map((employee) => (
                          <MenuItem key={employee.id} value={employee.id}>
                            {employee.first_name} {employee.last_name} - {employee.position}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Holiday</InputLabel>
                      <Select
                        value={holidayWorkForm.holiday_id}
                        onChange={(e) => setHolidayWorkForm({...holidayWorkForm, holiday_id: e.target.value})}
                        label="Holiday"
                      >
                        {holidays.map((holiday) => (
                          <MenuItem key={holiday.id} value={holiday.id}>
                            {holiday.name} - {new Date(holiday.date).toLocaleDateString()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Work Date"
                      type="date"
                      value={holidayWorkForm.date}
                      onChange={(e) => setHolidayWorkForm({...holidayWorkForm, date: e.target.value})}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      label="Hours Worked"
                      type="number"
                      inputProps={{ min: 0, max: 24, step: 0.5 }}
                      value={holidayWorkForm.hours_worked}
                      onChange={(e) => setHolidayWorkForm({...holidayWorkForm, hours_worked: parseFloat(e.target.value) || 0})}
                      fullWidth
                    />
                    <TextField
                      label="Notes"
                      value={holidayWorkForm.notes}
                      onChange={(e) => setHolidayWorkForm({...holidayWorkForm, notes: e.target.value})}
                      placeholder="Additional notes..."
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowHolidayWorkDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleRecordHolidayWork} disabled={loading} variant="contained">
                    Record Work
                  </Button>
                </DialogActions>
              </Dialog>
              {/* Search */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  placeholder="Search by employee or holiday name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </Box>

              {/* Holiday Work Records Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Holiday</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Hours</TableCell>
                      <TableCell>Holiday Pay</TableCell>
                      <TableCell>Additional Pay</TableCell>
                      <TableCell>Total Pay</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredHolidayWork.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {record.employee?.first_name} {record.employee?.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {record.employee?.position}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {record.holiday?.name}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              {record.holiday && getHolidayTypeBadge(record.holiday.type)}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {new Date(record.date).toLocaleDateString('en-PH')}
                        </TableCell>
                        <TableCell>{record.hours_worked} hrs</TableCell>
                        <TableCell>{formatCurrency(record.holiday_pay)}</TableCell>
                        <TableCell>{formatCurrency(record.additional_pay)}</TableCell>
                        <TableCell sx={{ fontWeight: 'medium' }}>
                          {formatCurrency(record.total_pay)}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteHolidayWork(record.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default HolidayManagement;